import type {
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Menu,
  Modal,
  Skeleton,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconChevronDown,
  IconDownload,
  IconEdit,
  IconPrinter,
  IconSignature,
  IconStar,
  IconStarOff,
} from '@tabler/icons-react';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';

import { useMedplum } from '@medplum/react-hooks';
import { useReactToPrint } from 'react-to-print';
import { notify } from '@/lib/notify';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';
import { blobFromBytes, sanitizeDownloadFilename, triggerBlobDownload } from '../../lib/fileDownload';
import { buildProgressNotePdfBytes } from '../../lib/progressNotePdf';
import { requestSignedNoteExport } from '../../lib/signedNoteExportService';
import { isMockQuestionnaireResponseId } from './mockAppointmentNoteData';
import { SignatureBlock } from '@/components/SignatureBlock';
import {
  CohortProgressNoteTitleExtras,
  type CohortNoteKind,
} from './components/CohortProgressNoteTitleExtras';
import type { SignatureInfo } from './fhirNoteMapping';
import {
  getReviewCommentsFromQR,
  getSignatureFromQR,
  qrStatusToDisplay,
  getItemAnswerText,
  findQuestionnaireItemText,
  PROVIDER_SIGNATURE_EXT_URL,
  SUPERVISOR_SIGNATURE_EXT_URL,
  type AiNoteCheckResult,
} from './fhirNoteMapping';
import { getNoteMetadataFromQR } from './noteMetadataMapping';
import type { NoteMetadataSnapshot } from '@/schemas/note-metadata.schema';
import type { EncounterSessionTiming } from '@/lib/encounterSessionTiming';
import {
  SessionMetadataSummaryReadonly,
} from './components/SessionMetadataWorkspace';
import { getStatusColor, formatStatusText } from '../../lib/statusUtils';
import { QRDocumentWithComments } from '../supervision/components/QRDocumentWithComments';
import { splitParticipantAttendanceFromResponseItems, getParticipantLocationFromResponseItems, getParticipantAttendanceFromResponseItems } from './utils/participantAttendance';
import type { ParticipantAttendanceFhirStatus } from './utils/participantAttendance';
import { useDocumentFormatPresentation } from '../settings/template-library/hooks';
import { DocumentFormatFooter } from '../settings/template-library/components/DocumentFormatBranding';
import type { Addendum } from './addendums';
import { CorrectedNoteChangeHistory } from './addendums';
import { ADDENDUM_REASON_OPTIONS } from './addendums/types';

const EMPTY_PLACEHOLDER = '\u2014'; // em dash

// ---------------------------------------------------------------------------
// QR-backed read-only item renderer
// ---------------------------------------------------------------------------

/**
 * Render a single QuestionnaireResponseItem in read-only mode.
 * Follows the same rendering logic as the Medplum QuestionnaireResponseItemDisplay
 * but uses our existing page styling (fw={600}, size="sm", c="gray.8").
 */
export function ResponseItemReadOnly({
  item,
  questionnaire,
}: {
  item: QuestionnaireResponseItem;
  questionnaire?: Questionnaire | null;
}): JSX.Element {
  const title =
    item.text ??
    (questionnaire && item.linkId
      ? findQuestionnaireItemText(questionnaire, item.linkId)
      : undefined) ??
    '';

  // Nested items (group-like)
  if (item.item && item.item.length > 0) {
    return (
      <Stack gap="md" className="questionnaire-readonly-section">
        {title ? (
          <>
            <Text fw={700} size="md" c="dark">
              {title}
            </Text>
            <Divider />
          </>
        ) : null}
        <Stack gap="md">
          {item.item.map((child, idx) => (
            <ResponseItemReadOnly
              key={child.linkId ?? `child-${idx}`}
              item={child}
              questionnaire={questionnaire}
            />
          ))}
        </Stack>
      </Stack>
    );
  }

  // Leaf item with answers
  const answerText = getItemAnswerText(item);

  return (
    <Stack gap={4}>
      {title ? (
        <Text fw={600} size="sm" c="gray.8">
          {title}
        </Text>
      ) : null}
      {answerText ? (
        answerText.split('\n').map((line, i) => (
          <Text key={i} size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
            {stripHtml(line) || EMPTY_PLACEHOLDER}
          </Text>
        ))
      ) : (
        <Text size="sm" c="gray.5">
          {EMPTY_PLACEHOLDER}
        </Text>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Helpers (shared utilities)
// ---------------------------------------------------------------------------

import { formatShortDateTime } from './utils/dateFormatters';
import { stripHtml } from './utils/stripHtml';

// ---------------------------------------------------------------------------
// Addendum summary section (bottom of note body for addended notes)
// ---------------------------------------------------------------------------

function getReasonLabel(code: string): string {
  return ADDENDUM_REASON_OPTIONS.find((o) => o.value === code)?.label ?? code;
}

function AddendumSummarySection({
  addendums,
  originalNoteDate: _originalNoteDate,
  originalAuthorName,
}: {
  addendums: Addendum[];
  originalNoteDate: string;
  originalAuthorName?: string;
}): JSX.Element {
  if (addendums.length === 0) return <></>;

  const latestAddendum = addendums[addendums.length - 1];
  const originalAuthor = originalAuthorName ?? latestAddendum.authorName;

  return (
    <Box
      mt="md"
      p="md"
      style={{
        borderRadius: 'var(--mantine-radius-md)',
        border: '1px solid var(--mantine-color-gray-3)',
        backgroundColor: 'var(--mantine-color-gray-0)',
      }}
    >
      <Text size="xs" fw={600} tt="uppercase" c="gray.6" mb="sm" style={{ letterSpacing: '0.04em' }}>
        Revised Note Summary
      </Text>
      <Stack gap="sm">
        {addendums.map((addendum) => {
          const authorLine = addendum.authorCredentials
            ? `${addendum.authorName}, ${addendum.authorCredentials}`
            : addendum.authorName;
          return (
            <Box key={addendum.id}>
              {addendums.length > 1 && (
                <Text size="sm" fw={600} c="gray.8" mb={4}>{addendum.label}</Text>
              )}
              <Group gap="lg" wrap="wrap">
                <Box>
                  <Text size="xs" c="gray.5">Original Note By</Text>
                  <Text size="sm" c="gray.8">{originalAuthor}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="gray.5">Original Note Date</Text>
                  <Text size="sm" c="gray.8">{formatShortDateTime(addendum.originalNoteDate)}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="gray.5">Revised Note By</Text>
                  <Text size="sm" c="gray.8">{authorLine}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="gray.5">Revised Note Date</Text>
                  <Text size="sm" c="gray.8">{formatShortDateTime(addendum.timestamp)}</Text>
                </Box>
              </Group>
              <Group gap="lg" wrap="wrap" mt="xs">
                <Box>
                  <Text size="xs" c="gray.5">Reason</Text>
                  <Text size="sm" c="gray.8">{getReasonLabel(addendum.reasonForChange)}</Text>
                </Box>
                {addendum.body?.trim() && (
                  <Box style={{ flex: 1, minWidth: 200 }}>
                    <Text size="xs" c="gray.5">Comment</Text>
                    <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap' }}>{addendum.body}</Text>
                  </Box>
                )}
              </Group>
              {addendums.length > 1 && addendum !== latestAddendum && <Divider mt="sm" />}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Note print/download preview
// ---------------------------------------------------------------------------

const PRINT_STYLE = `
  @media print {
    @page { size: letter; margin: 0.75in; }
    body { margin: 0; padding: 0; }
    .note-preview-document {
      box-shadow: none !important;
      border: none !important;
      margin: 0 !important;
      padding: 0 !important;
      width: auto !important;
      min-height: auto !important;
    }
    .note-preview-no-print { display: none !important; }
    .note-preview-sig-block { break-inside: avoid; }
    .note-preview-addendum-block { break-inside: avoid; }
    .note-preview-metadata-block { break-inside: avoid; }
  }
`;

const previewDocumentStyle: React.CSSProperties = {
  width: '8.5in',
  padding: '0.75in',
  backgroundColor: 'var(--mantine-color-white)',
  border: '1px solid var(--mantine-color-gray-3)',
  boxShadow: 'var(--mantine-shadow-sm)',
  borderRadius: 2,
  boxSizing: 'border-box' as const,
};

function NotePreviewContent({
  templateTitle,
  questionnaire,
  templateItems,
  noteMetadata,
  participantAttendanceRows = [],
  participantAttendanceByPatientId = {},
  participantLocationByPatientId = {},
  sessionTiming,
  providerSignature,
  supervisorSignature,
  showSupervisorBlock,
  addendums,
  originalNoteDate,
  originalAuthorName,
  footerText,
}: {
  templateTitle: string;
  questionnaire?: Questionnaire | null;
  templateItems: QuestionnaireResponseItem[];
  noteMetadata?: NoteMetadataSnapshot;
  participantAttendanceRows?: { patientId: string; displayName: string }[];
  participantAttendanceByPatientId?: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  participantLocationByPatientId?: Record<string, string | undefined>;
  sessionTiming?: EncounterSessionTiming;
  providerSignature?: SignatureInfo;
  supervisorSignature?: SignatureInfo;
  showSupervisorBlock: boolean;
  addendums?: Addendum[];
  originalNoteDate?: string;
  originalAuthorName?: string;
  footerText?: string;
}): JSX.Element {
  const qrItems = templateItems;
  const hasAddendums = addendums && addendums.length > 0 && originalNoteDate;

  return (
    <>
      <style>{PRINT_STYLE}</style>

      <div className="note-preview-document" style={previewDocumentStyle}>
        <Stack gap="md">
          <Title order={3}>{templateTitle}</Title>
          <Divider />

          {noteMetadata ? (
            <div className="note-preview-metadata-block">
              <SessionMetadataSummaryReadonly
                metadata={noteMetadata}
                attendanceRows={participantAttendanceRows}
                attendanceByPatientId={participantAttendanceByPatientId}
                locationByPatientId={participantLocationByPatientId}
                providerName={originalAuthorName}
                sessionTiming={sessionTiming}
              />
              <Divider />
            </div>
          ) : null}

          {qrItems.length > 0 && (
            <Stack gap="lg">
              {qrItems.map((qrItem, idx) => (
                <ResponseItemReadOnly
                  key={qrItem.linkId ?? `item-${idx}`}
                  item={qrItem}
                  questionnaire={questionnaire}
                />
              ))}
            </Stack>
          )}

          {hasAddendums && (
            <div className="note-preview-addendum-block">
              <AddendumSummarySection
                addendums={addendums}
                originalNoteDate={originalNoteDate}
                originalAuthorName={originalAuthorName}
              />
            </div>
          )}

          <Divider />

          <div className="note-preview-sig-block">
            <Grid>
              <Grid.Col span={{ base: 12, sm: showSupervisorBlock ? 6 : 12 }}>
                <SignatureBlock label="Provider" signature={providerSignature} />
              </Grid.Col>
              {showSupervisorBlock && (
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <SignatureBlock label="Supervisor" signature={supervisorSignature} />
                </Grid.Col>
              )}
            </Grid>
          </div>

          <DocumentFormatFooter footerText={footerText} />
        </Stack>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ProgressNoteCardProps {
  /** The QuestionnaireResponse to display (primary data source). */
  questionnaireResponse?: QuestionnaireResponse | null;
  /** The Questionnaire template (used for label fallback). */
  questionnaire?: Questionnaire | null;
  loading?: boolean;
  /** Whether to show the supervisor signature section */
  showSupervisorSignature?: boolean;
  /** Number of addendums attached to this signed note (shown as status indicator). */
  addendumCount?: number;
  /** Addendums for corrected note view (when set, corrected version is primary with change history). */
  addendums?: Addendum[];
  /** Original note date (ISO) for change history. */
  originalNoteDate?: string;
  /** Display name for the original note author (for addendum summary). */
  originalAuthorName?: string;
  /** Callback when Edit button is clicked. */
  onEdit?: () => void;
  /** Open the sign modal. When omitted, the Sign Note button is hidden. */
  onSignClick?: () => void;
  /** When provided, allow resolving review comments from this view (persist to QR). */
  onResolveComment?: (commentId: string) => void;
  /** When set, show a pencil icon for editing a signed note (creates addendum from diff). */
  onEditAddendum?: () => void;
  /** Soft warning about automatic note review before signing. */
  signReviewWarning?: string;
  /** Deterministic check results for inline warnings. */
  automaticChecks?: AiNoteCheckResult[];
  /** Encounter timing for metadata mismatch banner. */
  sessionTiming?: EncounterSessionTiming;
  /** Unify Admin only: show exemplar star and Mark/Unmark exemplar menu actions. */
  showExemplarControls?: boolean;
  isExemplarNote?: boolean;
  exemplarToggleLoading?: boolean;
  onToggleExemplar?: (mark: boolean) => void;
  /** Extra chip rendered next to the note title (e.g. transcript status). */
  headerBadge?: ReactNode;
  /** Cohort appointment: badge next to the note title. */
  cohortNoteKind?: CohortNoteKind;
  /** Link to the shared Group note when viewing an individual fan-out copy. */
  sharedNoteHref?: string;
}

/**
 * Read-only progress note card backed by a Medplum QuestionnaireResponse.
 * Renders QR items following the same logic as the Medplum
 * QuestionnaireResponseItemDisplay but with existing page styling.
 */
export function ProgressNoteCard({
  questionnaireResponse,
  questionnaire,
  loading,
  showSupervisorSignature = false,
  addendumCount = 0,
  addendums,
  originalNoteDate,
  originalAuthorName,
  onEdit,
  onSignClick,
  onResolveComment,
  onEditAddendum,
  signReviewWarning,
  sessionTiming,
  showExemplarControls = false,
  isExemplarNote = false,
  exemplarToggleLoading = false,
  onToggleExemplar,
  headerBadge,
  cohortNoteKind,
  sharedNoteHref,
}: ProgressNoteCardProps): JSX.Element {
  const medplum = useMedplum();
  const [downloading, setDownloading] = useState(false);
  const [changeHistoryModalOpened, { open: openChangeHistoryModal, close: closeChangeHistoryModal }] =
    useDisclosure(false);
  const printContentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: printContentRef });

  // Review comments from QR extension (must run before any early return to satisfy rules of hooks)
  // Depend on extension so when a comment is resolved (QR updated in place) comments recompute.
  const comments = useMemo(
    () => (questionnaireResponse ? getReviewCommentsFromQR(questionnaireResponse) : []),
    [questionnaireResponse],
  );

  // --- Determine note state (hooks must run before any early return) ---
  const hasNote = questionnaireResponse !== null && questionnaireResponse !== undefined;
  const { templateItems, attendanceItem } = useMemo(
    () => splitParticipantAttendanceFromResponseItems(questionnaireResponse?.item),
    [questionnaireResponse?.item],
  );
  const qrItems = templateItems;
  const savedNoteMetadata = useMemo(
    () => (questionnaireResponse ? getNoteMetadataFromQR(questionnaireResponse) : undefined),
    [questionnaireResponse],
  );
  const { attendanceReadOnly, attendanceRows, locationReadOnly } = useMemo(() => {
    const m = attendanceItem
      ? getParticipantAttendanceFromResponseItems([attendanceItem])
      : {};
    const rows =
      attendanceItem?.item
        ?.map((c) => {
          const prefix = 'participant-attendance.';
          const id = c.linkId?.startsWith(prefix) ? c.linkId.slice(prefix.length) : '';
          return { patientId: id, displayName: c.text ?? id };
        })
        .filter((r) => r.patientId) ?? [];
    const location = getParticipantLocationFromResponseItems(questionnaireResponse?.item);
    return { attendanceReadOnly: m, attendanceRows: rows, locationReadOnly: location };
  }, [attendanceItem, questionnaireResponse?.item]);

  const providerSignature = useMemo(
    () =>
      questionnaireResponse
        ? getSignatureFromQR(questionnaireResponse, PROVIDER_SIGNATURE_EXT_URL)
        : undefined,
    [questionnaireResponse],
  );
  const supervisorSignature = useMemo(
    () =>
      questionnaireResponse
        ? getSignatureFromQR(questionnaireResponse, SUPERVISOR_SIGNATURE_EXT_URL)
        : undefined,
    [questionnaireResponse],
  );
  const templateTitle = questionnaire?.title ?? 'Progress Note';
  const handleDownload = useCallback(async () => {
    const qr = questionnaireResponse;
    const qrId = qr?.id;
    if (!qrId || !qr) return;

    const safeName = sanitizeDownloadFilename(templateTitle, '.pdf');
    setDownloading(true);
    try {
      const bytes = buildProgressNotePdfBytes(qr, questionnaire, templateTitle);
      triggerBlobDownload(blobFromBytes(bytes, 'application/pdf'), safeName);

      if (providerSignature && !isMockQuestionnaireResponseId(qrId)) {
        void requestSignedNoteExport(medplum, qrId).catch(() => undefined);
      }
    } catch (err) {
      notify({
        type: 'warning',
        title: 'Download unavailable',
        message:
          getUserFacingErrorMessage(err, 'Could not download this note as PDF. Try printing instead.'),
      });
    } finally {
      setDownloading(false);
    }
  }, [medplum, providerSignature, questionnaire, questionnaireResponse, templateTitle]);

  const docFormat = useDocumentFormatPresentation({
    requiresCosignature: showSupervisorSignature,
    supervisorSignature,
  });
  const showSupervisorBlock =
    !!supervisorSignature || (showSupervisorSignature && docFormat.addSupervisorInfo);

  // --- Loading skeleton ---
  if (loading) {
    return (
      <Card withBorder>
        <Stack gap="md">
          <Skeleton height={28} width={200} radius="sm" />
          <Skeleton height={16} width="100%" radius="sm" />
          <Skeleton height={16} width="90%" radius="sm" />
          <Skeleton height={16} width="80%" radius="sm" />
          <Skeleton height={16} width="100%" radius="sm" />
          <Skeleton height={16} width="70%" radius="sm" />
          <Skeleton height={60} radius="sm" />
        </Stack>
      </Card>
    );
  }

  // Display status
  const displayStatus = questionnaireResponse ? qrStatusToDisplay(questionnaireResponse) : undefined;

  // Error state: QR exists but template couldn't be loaded
  const templateMissing = hasNote && !questionnaire;

  return (
    <Card withBorder>
      <Stack gap="md">
        {/* Header with title and actions */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm" align="center" wrap="wrap">
            {showExemplarControls && isExemplarNote && (
              <IconStar size={20} color="var(--mantine-color-gold-6)" fill="var(--mantine-color-gold-6)" aria-label="Exemplar note" />
            )}
            <Title order={3}>{templateTitle}</Title>
            {headerBadge}
            {cohortNoteKind ? (
              <CohortProgressNoteTitleExtras
                noteKind={cohortNoteKind}
                sharedNoteHref={sharedNoteHref}
              />
            ) : null}
            {displayStatus && (
              <Badge
                color={getStatusColor(displayStatus)}
                variant="light"
                size="sm"
              >
                {formatStatusText(displayStatus)}
              </Badge>
            )}
            {addendumCount > 0 && (
              <UnstyledButton
                onClick={openChangeHistoryModal}
                aria-label={`View ${addendumCount} revised note${addendumCount !== 1 ? 's' : ''}`}
              >
                <Text size="xs" c="gold.7" fw={500} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                  {addendumCount} Revised Note{addendumCount !== 1 ? 's' : ''}
                </Text>
              </UnstyledButton>
            )}
          </Group>

          <Group gap="xs">
            {hasNote && onEdit && !providerSignature && (
              <Button
                leftSection={<IconEdit size={14} />}
                onClick={onEdit}
              >
                Edit
              </Button>
            )}
            {hasNote && onEditAddendum ? (
              <Button
                variant="outline"
                color="gold"
                leftSection={<IconEdit size={14} />}
                onClick={onEditAddendum}
              >
                Revise note
              </Button>
            ) : null}
            <Menu position="bottom-end" shadow="md" width={showExemplarControls && onToggleExemplar ? 240 : 160}>
              <Menu.Target>
                <Button
                  variant="default"
                  rightSection={<IconChevronDown size={14} />}
                >
                  More
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {showExemplarControls && onToggleExemplar && hasNote && (
                  isExemplarNote ? (
                    <Menu.Item
                      leftSection={<IconStarOff size={16} />}
                      onClick={() => onToggleExemplar(false)}
                      disabled={exemplarToggleLoading}
                    >
                      Unmark note as exemplar
                    </Menu.Item>
                  ) : (
                    <Menu.Item
                      leftSection={<IconStar size={16} />}
                      onClick={() => onToggleExemplar(true)}
                      disabled={exemplarToggleLoading}
                    >
                      Mark note as exemplar
                    </Menu.Item>
                  )
                )}
                <Menu.Item leftSection={<IconPrinter size={16} />} onClick={() => handlePrint()}>
                  Print
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconDownload size={16} />}
                  onClick={() => void handleDownload()}
                  disabled={downloading}
                >
                  Download
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>

        <Divider />

        {/* No note state */}
        {!hasNote && (
          <Box py="xl" ta="center">
            <Text c="gray.5" size="sm">
              No progress note has been created for this appointment yet.
            </Text>
          </Box>
        )}

        {/* Template load error */}
        {templateMissing && (
          <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
            The template for this note could not be loaded. Some labels may be missing.
          </Alert>
        )}

        {hasNote && savedNoteMetadata ? (
          <Box mb="md">
            <SessionMetadataSummaryReadonly
              metadata={savedNoteMetadata}
              attendanceRows={attendanceRows}
              attendanceByPatientId={attendanceReadOnly}
              locationByPatientId={locationReadOnly}
              providerName={originalAuthorName ?? providerSignature?.name}
              sessionTiming={sessionTiming}
              onEdit={undefined}
            />
          </Box>
        ) : null}

        {hasNote && qrItems.length > 0 && (
          <QRDocumentWithComments
            questionnaireResponse={{ ...questionnaireResponse!, item: templateItems }}
            questionnaire={questionnaire ?? null}
            comments={comments}
            onAddComment={() => { }}
            onResolveComment={onResolveComment}
            isSigned={!!providerSignature}
            readOnly
            allowResolveInReadOnly={!!onResolveComment}
            hideTitle
            showLineNumbers
          />
        )}

        {hasNote &&
          qrItems.length === 0 &&
          !templateMissing &&
          !attendanceItem && (
            <Text size="sm" c="gray.5" ta="center" py="md">
              Note content is not yet available.
            </Text>
          )}

        {/* Addendum summary at bottom of note body */}
        {hasNote && addendums && addendums.length > 0 && originalNoteDate && (
          <AddendumSummarySection
            addendums={addendums}
            originalNoteDate={originalNoteDate}
            originalAuthorName={originalAuthorName}
          />
        )}

        {/* Signatures */}
        {hasNote && (
          <>
            <Divider />
            <Grid>
              <Grid.Col span={{ base: 12, sm: showSupervisorBlock ? 6 : 12 }}>
                {providerSignature ? (
                  <SignatureBlock
                    label="Provider"
                    signature={providerSignature}
                  />
                ) : onSignClick ? (
                  <Stack gap="sm" align="stretch">
                    {signReviewWarning?.trim() ? (
                      <Alert color="yellow" variant="light" radius="md">
                        <Text size="xs" c="gray.7" lh={1.5}>
                          {signReviewWarning}
                        </Text>
                      </Alert>
                    ) : null}
                    <Group justify="flex-end">
                      <Button
                        leftSection={<IconSignature size={16} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSignClick();
                        }}
                      >
                        Sign Note
                      </Button>
                    </Group>
                  </Stack>
                ) : null}
              </Grid.Col>
              {showSupervisorBlock && (
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <SignatureBlock
                    label="Supervisor"
                    signature={supervisorSignature}
                  />
                </Grid.Col>
              )}
            </Grid>
            <DocumentFormatFooter footerText={docFormat.footerText} />
          </>
        )}
      </Stack>

      {hasNote && questionnaireResponse && (
        <Box display="none" aria-hidden>
          <Box ref={printContentRef}>
            <NotePreviewContent
              templateTitle={templateTitle}
              questionnaire={questionnaire}
              templateItems={qrItems}
              noteMetadata={savedNoteMetadata}
              participantAttendanceRows={attendanceRows}
              participantAttendanceByPatientId={attendanceReadOnly}
              participantLocationByPatientId={locationReadOnly}
              sessionTiming={sessionTiming}
              providerSignature={providerSignature}
              supervisorSignature={supervisorSignature}
              showSupervisorBlock={showSupervisorBlock}
              addendums={addendums}
              originalNoteDate={originalNoteDate}
              originalAuthorName={originalAuthorName}
              footerText={docFormat.footerText}
            />
          </Box>
        </Box>
      )}

      {/* Change history modal */}
      <Modal
        opened={changeHistoryModalOpened}
        onClose={closeChangeHistoryModal}
        title="Change History"
        size="lg"
        centered
      >
        {addendums && addendums.length > 0 && originalNoteDate ? (
          <CorrectedNoteChangeHistory addendums={addendums} originalNoteDate={originalNoteDate} />
        ) : (
          <Text size="sm" c="gray.5" ta="center" py="md">No change history available.</Text>
        )}
      </Modal>
    </Card>
  );
}
